import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Box from '@mui/material/Box'
import useIsMobile from '../hooks/useIsMobile'

// Pickers and confirmations. A bottom sheet on a phone, a centered dialog on a
// desktop, one component either way.
//
// It replaces Popup, which was a bare MUI Modal wrapping a Box with
// `width: fit-content` and 32px of padding at every viewport. That shape had
// three separate faults on a phone. It had no max-height below 600px, so a tall
// one grew past both edges of the screen and the unreachable top was where the
// title lived. It rendered edge to edge with its inner min-width eating the left
// padding. And it floated in the middle of the screen, which is the one place a
// thumb cannot comfortably reach -- the location picker was 195x269px, 63% empty
// space, for a choice between two stores.
//
// Building on Dialog rather than Modal also brings the things Popup never had:
// a labelled dialog role, a focus trap, scroll locking, and Escape.

// Tall enough to feel like a sheet, short enough that the page behind it still
// shows -- which is what says "this is a layer, not a new screen".
const MAX_HEIGHT = '85svh'

export default function Sheet(props) {
    const { open, onClose, title, actions, size = 'xs', fullScreen = false, children } = props
    const isMobile = useIsMobile()

    // A live camera viewport is not a layer over the page, it is the screen: the
    // 85svh cap would leave the page showing behind the video, and the handle
    // would promise a drag gesture that does nothing.
    const paperSx = fullScreen
        ? { maxHeight: '100%', borderRadius: 0 }
        : {
            maxHeight: MAX_HEIGHT,
            ...(isMobile
                ? {
                    margin: 0,
                    width: '100%',
                    maxWidth: '100%',
                    borderRadius: '16px 16px 0px 0px'
                }
                : { borderRadius: '12px' })
        }

    return (
        <Dialog
            open={Boolean(open)}
            onClose={onClose}
            fullWidth
            maxWidth={size}
            fullScreen={fullScreen}
            // Bottom-anchored on a phone; MUI centers by default.
            sx={{ '& .MuiDialog-container': { alignItems: isMobile && !fullScreen ? 'flex-end' : 'center' } }}
            PaperProps={{ sx: paperSx }}
        >
            {isMobile && !fullScreen && (
                <Box
                    aria-hidden
                    data-testid='sheet-handle'
                    sx={{
                        width: 36,
                        height: 4,
                        borderRadius: 2,
                        bgcolor: 'divider',
                        margin: '8px auto 0'
                    }}
                />
            )}

            {title && <DialogTitle sx={{ fontWeight: 600 }}>{title}</DialogTitle>}

            {/* The body is the only part that scrolls, so the title stays put and
                the actions stay reachable however long the content runs.
                The padding-top is restored deliberately: MUI zeroes it whenever a
                DialogTitle precedes the content, which clips the floating label of
                an outlined Select sitting at the top -- which is exactly what every
                one of these pickers puts there. The &-prefix is load-bearing too,
                since MUI's own rule is a two-class selector. */}
            <DialogContent sx={{ overflowY: 'auto', '&.MuiDialogContent-root': { pt: 2 } }}>
                {children}
            </DialogContent>

            {actions && (
                <DialogActions
                    sx={{
                        px: 3,
                        pb: 'calc(16px + env(safe-area-inset-bottom))',
                        // ActionBar owns its own layout; DialogActions must not
                        // re-space or right-align what it is given.
                        '& > :not(style)': { width: '100%' }
                    }}
                >
                    {actions}
                </DialogActions>
            )}
        </Dialog>
    )
}
